import zod from 'zod';
import { useCallback, useReducer, useEffect, useContext } from 'react';
import { ApplicationState as QuestionReducerState, QuestionState, SectionState } from '@aidkitorg/types/lib/convoai';
import { ChatContext } from '../Contexts/ChatContext';
import slugid from 'slugid';
import { v4 } from 'uuid';
import { useLogger } from '../Util';

export type QuestionStateEvent = {
  type: 'change',
  questionId: string,
  update: Partial<QuestionState>
} | {
  type: 'update-section',
  sectionId: string,
  update: Partial<SectionState>
}

export function useQuestionStateReducer(initial?: QuestionReducerState) {
  const chat = useContext(ChatContext);
  const logger = useLogger('QuestionStateReducer');

  const [state, dispatch] = useReducer((state: QuestionReducerState, event: QuestionStateEvent) => {
    switch (event.type) {
      case 'change':
        let currentQuestion = state.currentQuestion;
        const question = state.questions[event.questionId];
        if (question?.isNext) {
          currentQuestion = { ...question, ...event.update };
        }
        return {
          ...state,
          currentQuestion,
          questions: {
            ...state.questions,
            [event.questionId]: {
              ...(state.questions[event.questionId] ?? {}),
              ...event.update
            }
          }
        };
      case 'update-section':
        let currentSection = state.currentSection;
        const section = state.sections[event.sectionId];
        if (section?.viewing) {
          currentSection = { ...section, ...event.update };
        }
        return {
          ...state,
          currentSection,
          sections: {
            ...state.sections,
            [event.sectionId]: {
              ...(state.sections[event.sectionId] ?? {}),
              ...event.update
            }
          }
        };
      default:
        return state;
    }
  }, initial ?? { questions: {}, sections: {} });

  const questionChanged = useCallback((event: QuestionStateEvent) => {
    dispatch(event);
  }, [dispatch, chat.sendMessage]);

  useEffect(() => {

    const { sections, questions } = state;

    chat.sendMessage({
      id: '__app_state__',
      type: 'message',
      role: 'system',
      silent: true,
      content: [
        {
          type: 'text',
          content: JSON.stringify({
            allSections: Object.values(sections),
            allQuestions: Object.values(questions),
            currentSection: state.currentSection,
            currentQuestion: state.currentQuestion
          })
        }
      ],
      meta: {
        time: Date.now()
      }
    });

  }, [state.sections, state.questions]);

  return {
    questionChanged
  };
}
